import { CheckIcon } from "@heroicons/react/outline";
import * as Sentry from "@sentry/nextjs";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React from "react";

import { DialogEnum } from "@/consts/DialogEnum";
import { getUnfollowUserButtonEvent } from "@/features/auth/analytics/helper";
import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import { useUIContext } from "@/features/common/hooks/UIContext/useUIContext";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useHandleGraphqlError } from "@/features/common/hooks/useHandleGraphqlError";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  FollowUserDocDocument,
  useUnfollowUserMutation,
} from "@/generated/generated-hooks";

import { FollowUserButtonProps } from "./type";

export const UnfollowUserButton: React.FC<FollowUserButtonProps> = (props) => {
  const { writerId, isOnOverlay, utilityClassName = "h-30", fullWidth } = props;
  const router = useRouter();
  const { user, ready } = useUserContext();
  const { openDialog } = useUIContext();
  const handleGraphqlError = useHandleGraphqlError();

  const [unfollowUser, { loading }] = useUnfollowUserMutation({
    update: (cache, result) => {
      cache.writeQuery({
        query: FollowUserDocDocument,
        variables: { input: writerId },
        data: { followUserDoc: null },
      });
    },
  });

  const { t } = useTranslation("common");

  const onClickButton = async () => {
    firebase
      .analytics()
      .logEvent(getUnfollowUserButtonEvent(router), { writerId });

    if (!user) {
      openDialog(DialogEnum.Login);
      return;
    }

    const variables = { input: writerId };
    try {
      await unfollowUser({ variables });
    } catch (e) {
      handleGraphqlError({
        e,
        sentryCaptureContext: {
          tags: {
            mutations: "useUnfollowUserMutation",
            writerId,
          },
        },
      });
    }
  };

  return (
    <OutlinedButton
      variant={isOnOverlay ? "overlay-white-transparent" : "secondary"}
      buttonProps={{ disabled: !ready || loading, onClick: onClickButton }}
      utilityClassName={utilityClassName}
      fullWidth={fullWidth}
    >
      <CheckIcon className="w-4 h-4 mr-1" />
      {t("common:follow.buttonLabel.followed")}
    </OutlinedButton>
  );
};
